// @ts-nocheck
import React, { useState, useCallback, useEffect, useRef } from 'react';

// Styles
import './UnicoCapture.css';
import './UnicoCapture.page.css';

// Context
import { useSignature } from '../../../../../hooks/SignatureContext';
import { useRetentionSignature } from '../../../../../hooks/RetentionSignatureContext';
import { useUnicoContext } from '../../../../../hooks/useUnicoContext';

// Service
import SignatureService from '../../../../../services/SignatureService';

// Components
import { Button } from '../../../../../components/UISignatue/Button';
import { ErrorPermissions } from '../../../ScreensError/index';
import Alert from '../../../../../components/UI/Alert/Alert';

// Utils
import logger from '../../../../../utils/logger.utils';

// SVG
import { IconErrorPermissionCam } from '../../../ScreensError/svg';
import { ArrowRight, ArrowLeft } from '../../../Cam/svgs';

// Styles
import {
  Spinner,
  LoadingText,
  ErrorMessage,
  ResultCard,
  ResultTitle,
  ResultMessage,
  LivenessStatus,
  Preview,
  IdleState,
  IdleIllustration,
  IdleTitle,
  IdleDescription,
} from './UnicoCapture.styles';

interface UnicoCaptureProp {
  formValues: any;
  isRetention?: boolean;
}

const UnicoCaptureComponent = ({
    formValues,
    isRetention = false,
  }: UnicoCaptureProp) => {
    const { setStep, setFormOpened, setShowModal, formOpened, errorCam } = isRetention
      ? useRetentionSignature()
      : useSignature();

    const { status, error, result, openLivenessCapture, resetCapture, initializeSession } = useUnicoContext();

    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [captureError, setCaptureError] = useState<string | null>(null);
    const autoSentRef = useRef(false);

    const isLoading = status === 'awaiting_permission';
    const sdkActive = status === 'camera_active' || status === 'processing';

    useEffect(() => {
      initializeSession();
    }, [initializeSession]);

    const handleStartCapture = useCallback(async () => {
      setCaptureError(null);
      resetCapture(); // clear any previous context error/state before re-opening

      try {
        // Check for explicit denial first (avoid getUserMedia prompt if already denied)
        if (navigator.permissions) {
          const permission = await navigator.permissions.query({ name: 'camera' as PermissionName });
          if (permission.state === 'denied') {
            setCaptureError('Permissão de câmera negada. Libere o acesso nas configurações do navegador e tente novamente.');
            return;
          }
        }

        // Always probe camera via getUserMedia before handing off to SDK.
        // This ensures: (1) permission dialog is shown if needed, (2) hardware
        // is confirmed accessible, (3) tracks are fully stopped so the SDK
        // gets a clean camera acquire — avoids race condition on quick grants.
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach((t) => t.stop());

        // Wait for the camera hardware to fully release before SDK reopens it.
        // 1200ms covers slow MIUI/Xiaomi devices that are late releasing the camera.
        await new Promise((r) => setTimeout(r, 1200));
      } catch (err) {
        logger.error('[UnicoCapture] Camera permission denied', err);
        setCaptureError('Permissão de câmera negada. Libere o acesso nas configurações do navegador e tente novamente.');
        return;
      }

      openLivenessCapture().catch((err) => {
        logger.error('[UnicoCapture] Failed to open liveness capture', err);
        setCaptureError('Falha ao abrir câmera. Tente novamente.');
      });
    }, [openLivenessCapture, resetCapture]);

  const handleSendCapture = useCallback(async () => {
    if (!result?.encrypted || !result?.raw_base64) {
      setCaptureError('Erro ao processar resultado da captura. Tente novamente.');
      return;
    }

    setSending(true);
    setCaptureError(null);

    try {
      let base64Photo = result.raw_base64;
      if (base64Photo.includes('base64,')) {
        [, base64Photo] = base64Photo.split('base64,');
      }

      const response = await SignatureService.sendSelfie({
        token: formOpened.token,
        base64: base64Photo,
        encrypted: result.encrypted,
        isRetentionPortability: isRetention,
      });

      if (response.data?.Id) {
        setFormOpened((prev: any) => ({ ...prev, selfieId: response.data.Id }));
        setSent(true);
      } else {
        setCaptureError('Erro ao confirmar envio. Tente novamente.');
      }
    } catch (err) {
      logger.error('Erro ao enviar captura Unico', err);
      setCaptureError(
        err?.response?.data?.message || 'Erro ao enviar captura. O token pode ter expirado (TTL: 60s).',
      );
    } finally {
      setSending(false);
    }
  }, [result, isRetention, formOpened.token, setFormOpened, setStep]);

  const handlePrevStep = () => {
    resetCapture();
    setStep((previousStep: any) => previousStep - 1);
    setShowModal(false);
  };

  const handleRetryCapture = useCallback(() => {
    setCaptureError(null);
    setSent(false);
    autoSentRef.current = false;
    resetCapture();
  }, [resetCapture]);

  useEffect(() => {
    if (!result?.encrypted || !result?.raw_base64) return;
    if (autoSentRef.current) return;
    if (sending || sent || captureError) return;
    autoSentRef.current = true;
    handleSendCapture();
  }, [result, sending, sent, captureError, handleSendCapture]);

  if (errorCam) {
    return (
      <ErrorPermissions
        titlePage={'Liveness'}
        cardTitle={'Sem acesso à câmera'}
        cardFooterlink={'https://support.google.com/chrome/answer/2693767?hl=pt-BR&co=GENIE.Platform%3DAndroid&oco=0'}
        cardFooterText={'Como permitir acesso à câmera'}
        icon={IconErrorPermissionCam}
      >
        Precisamos de acesso à câmera para capturar sua biometria com segurança.
        <br />
        <br />
        Libere o acesso à câmera para continuar
      </ErrorPermissions>
    );
  }

  const displayError = captureError || error;

  return (
    <div className={`unico-capture-page ${sdkActive ? 'unico-capture-page--active' : 'unico-capture-page--idle'}`}>
      <header className="unico-capture-topbar">
        <button className="unico-capture-back" onClick={handlePrevStep}>
          <ArrowLeft />
        </button>
        <h1>Captura Biométrica</h1>
      </header>

      {/* box-camera is ALWAYS in the DOM — SDK needs it mounted before open() is called */}
      <div className="unico-capture-body">
        <div
          id="box-camera"
          className={sdkActive ? 'box-camera-active' : 'box-camera-idle'}
        />
        {sdkActive && <div className="unico-capture-mask" />}
        {isLoading && (
          <div className="unico-capture-loading-overlay">
            <Spinner />
            <LoadingText>Abrindo câmera...</LoadingText>
          </div>
        )}

        {!sdkActive && displayError && !result && (
          <Alert className="ErrorAlert unico-capture-alert" title="Erro na captura" red>
            <ErrorMessage>{displayError}</ErrorMessage>
          </Alert>
        )}

        {!sdkActive && !result && !displayError && !sending && !sent && (
          <IdleState>
            <IdleIllustration>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </IdleIllustration>
            <IdleTitle>Verificação biométrica</IdleTitle>
            <IdleDescription>
              Vamos capturar sua biometria facial para confirmar sua identidade com segurança.
            </IdleDescription>
          </IdleState>
        )}

        {result && (
          <div className="unico-capture-success">
            {result.raw_base64 && (
              <Preview
                src={
                  result.raw_base64.startsWith('data:')
                    ? result.raw_base64
                    : `data:image/jpeg;base64,${result.raw_base64}`
                }
                alt="Captura biométrica"
              />
            )}
            {sending && (
              <ResultCard>
                <Spinner />
                <ResultTitle>Enviando captura...</ResultTitle>
                <ResultMessage>Aguarde enquanto registramos sua biometria.</ResultMessage>
              </ResultCard>
            )}
            {!sending && sent && (
              <ResultCard>
                <ResultTitle>Captura enviada com sucesso</ResultTitle>
                <ResultMessage>Sua biometria foi registrada.</ResultMessage>
              </ResultCard>
            )}
            {!sending && !sent && captureError && (
              <Alert className="ErrorAlert unico-capture-alert" title="Erro no envio" red>
                <ErrorMessage>{captureError}</ErrorMessage>
              </Alert>
            )}
          </div>
        )}
      </div>

      <footer className="unico-capture-footer">
        <div className="unico-capture-actions">
          {!result && !sending && !sent && !sdkActive && (
            <Button
              black
              big
              onClick={handleStartCapture}
              iconRight={ArrowRight}
              style={{ width: '100%', margin: 0 }}
              disabled={status === 'sdk_loading'}
            >
              {displayError ? 'Tentar Novamente' : 'Iniciar Captura'}
            </Button>
          )}

          {result && captureError && !sending && !sent && (
            <div className="unico-capture-result-actions">
              <Button transparent big onClick={handleRetryCapture} style={{ flex: 1, margin: 0 }}>
                Refazer Captura
              </Button>
            </div>
          )}

          {sent && !sending && (
            <Button
              black
              big
              onClick={() => setStep((prev: any) => prev + 1)}
              iconRight={ArrowRight}
              style={{ width: '100%', margin: 0 }}
            >
              Continuar
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
};

export const UnicoCapture = UnicoCaptureComponent;

export default UnicoCapture;
